import React, { useState, useEffect } from "react";
import SEO from "../seo";
const CustomizeYourCheckout = () => {
  const [activeSection, setActiveSection] = useState("customize-checkout");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "customize-checkout",
        "cod-configuration",
        "tipping-configuration",
        "best-practices",
        "preview-overview",
        "preview-store",
        "test-checkout",
        "debug-issues",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Customize Your Checkout Settings – xPAGE Help Center"
        description="Learn how to fully control your checkout experience on xPage, including COD configuration, tipping options, best practices, previewing your store, testing checkout, and debugging issues."
        keywords="xPAGE, customize checkout, COD setup, tipping options, best practices, preview store, test checkout, debug issues"
        canonical="https://help.xpage.ai/help/landing-page-compliance/Customize%20Your%20Checkout%20Settings"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Customize Checkout Header */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Customize Your Checkout Settings
          </h1>
          <p className="text-sm text-systemLabel mb-6">
            xPage allows you to fully control your checkout experience to
            optimize conversions and provide a seamless customer journey.
          </p>
        </header>

        {/* Steps to Customize Checkout */}
        <section id="customize-checkout" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Steps to Customize Checkout
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your <strong>xPage Admin</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to <strong>Settings</strong> →{" "}
                  <strong>Checkout</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Adjust the checkout behavior to suit your store:
                </span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>
                    Require customers to create an account or allow{" "}
                    <strong>guest checkout</strong>.
                  </li>
                  <li>
                    Decide whether to make <strong>phone number</strong>{" "}
                    mandatory.
                  </li>
                  <li>
                    Enable <strong>order notes</strong> or{" "}
                    <strong>delivery instructions</strong> for extra
                    flexibility.
                  </li>
                </ul>
              </li>
            </ol>
          </div>
        </section>

        {/* COD Form Configuration */}
        <section id="cod-configuration" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            COD Form Configuration
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Choose which fields are required for Cash on Delivery (COD)
                orders, such as full name, phone number, and address details.
              </li>
              <li>
                Optional fields can include country, city, street address, and
                ZIP code, depending on your shipping regions.
              </li>
            </ul>
          </div>
        </section>

        {/* Tipping Configuration */}
        <section id="tipping-configuration" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Tipping Configuration
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Enable tipping at checkout to allow customers to add gratuity.
              </li>
              <li>
                Customize the tip description and options for a personalized
                checkout experience.
              </li>
              <li>
                Click <strong>Save</strong> to apply your changes.
              </li>
            </ul>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> xPage provides
                pre-set options for all fields, making it easy and flexible to
                configure checkout quickly while maintaining an optimized user
                experience.
              </p>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section id="best-practices" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Best Practices
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Test each payment method before going live to ensure smooth
                transactions.
              </li>
              <li>
                Clearly display accepted payment methods on product pages and at
                checkout.
              </li>
              <li>
                For COD, communicate payment terms clearly to avoid failed
                deliveries.
              </li>
              <li>
                Keep checkout simple with fewer required fields often means
                higher conversion rates.
              </li>
            </ul>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-systemBorder my-8"></div>

        {/* Preview and Debug Header */}
        <header className="mb-6">
          <h2 className="text-lg font-semibold text-systemTitle mb-1">
            Preview and Debug Your Store
          </h2>
          <p className="text-sm text-systemLabel mb-6">
            Before you launch, it's important to review your store's design,
            test its features, and fix any issues. xPage lets you preview your
            store exactly as customers will see it, so you can make adjustments
            before going live.
          </p>
        </header>

        {/* Overview */}
        <section id="preview-overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">In xPage, you can:</p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Preview your store on desktop and mobile.</li>
              <li>Test product pages, checkout, and navigation.</li>
              <li>Identify and fix design or content errors.</li>
              <li>Validate payment, shipping, and tax settings.</li>
            </ul>
          </div>
        </section>

        {/* Preview your store */}
        <section id="preview-store" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Preview Your Store
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to the xPage Admin</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Go to Online Store then Themes
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Next to your active theme, click Preview
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Use the device toggle to switch between Desktop View and
                  Mobile View
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate through your store as a customer would browse
                  collections, view product pages, add products to the cart, and
                  proceed to checkout
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Test your checkout process */}
        <section id="test-checkout" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Test Your Checkout Process
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Add a test product to your cart
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Proceed to checkout</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Test:</span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>Shipping rate calculation.</li>
                  <li>Tax calculation (if enabled).</li>
                  <li>Payment method functionality.</li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  If possible, place a small test order to confirm the entire
                  flow
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> When testing
                payments, some gateways like Stripe and PayPal offer "test mode"
                to avoid actual charges.
              </p>
            </div>
          </div>
        </section>

        {/* Debug common issues */}
        <section id="debug-issues" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Debug Common Issues
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="overflow-x-auto">
              <table className="min-w-full border border-systemBorder">
                <thead>
                  <tr className="bg-system200">
                    <th className="border border-systemBorder px-4 py-3 text-left text-sm font-semibold text-systemTitle">
                      Issue
                    </th>
                    <th className="border border-systemBorder px-4 py-3 text-left text-sm font-semibold text-systemTitle">
                      Possible cause
                    </th>
                    <th className="border border-systemBorder px-4 py-3 text-left text-sm font-semibold text-systemTitle">
                      Solution
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-systemLabel">
                      Images appear blurry
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Low-resolution uploads
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Replace with high-quality images (min. 1200px width)
                    </td>
                  </tr>
                  <tr className="bg-systemBackground">
                    <td className="border border-systemBorder px-4 py-3 text-sm text-systemLabel">
                      Wrong currency showing
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Default currency not updated
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Check Settings then Store Info then Currency
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-systemLabel">
                      Checkout not loading
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Payment gateway not connected
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Reconnect payment provider in Settings then Payments
                    </td>
                  </tr>
                  <tr className="bg-systemBackground">
                    <td className="border border-systemBorder px-4 py-3 text-sm text-systemLabel">
                      Menu links broken
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Page not linked properly
                    </td>
                    <td className="border border-systemBorder px-4 py-3 text-sm text-system600">
                      Go to Online Store then Menus and fix URL or link target
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span>
              </p>
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1 text-sm text-systemLabel">
                <li>
                  Check on multiple browsers (Chrome, Safari, Edge) for
                  compatibility.
                </li>
                <li>
                  Test on both desktop and mobile devices for responsive design.
                </li>
                <li>
                  Ask a friend or colleague to test the store for a fresh
                  perspective.
                </li>
                <li>
                  Keep a checklist of fixes and mark them off before launch.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden lg:block lg:w-64 flex-shrink-0">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#customize-checkout"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customize-checkout"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customize Checkout
              </a>
            </li>
            <li>
              <a
                href="#cod-configuration"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "cod-configuration"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                COD Configuration
              </a>
            </li>
            <li>
              <a
                href="#tipping-configuration"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tipping-configuration"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tipping Configuration
              </a>
            </li>
            <li>
              <a
                href="#best-practices"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "best-practices"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Best Practices
              </a>
            </li>
            <li>
              <a
                href="#preview-overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "preview-overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Preview Overview
              </a>
            </li>
            <li>
              <a
                href="#preview-store"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "preview-store"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Preview Store
              </a>
            </li>
            <li>
              <a
                href="#test-checkout"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "test-checkout"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Test Checkout
              </a>
            </li>
            <li>
              <a
                href="#debug-issues"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "debug-issues"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Debug Issues
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CustomizeYourCheckout;
